// Keyframed knife animations for the rig arm, modelled on the CS:S knife (v_knife sequences):
// draw, idle, midslash1 (forehand, right to left), midslash2 (backhand, left to right), stab
// (wind up high, plunge forward and down) and an inspect. A pose is the arm swinging around the
// shoulder plus the wrist bending and twisting the blade. Camera space: X right, Y up, -Z forward.

export type KnifeAnim = 'draw' | 'idle' | 'slash1' | 'slash2' | 'stab' | 'inspect';

/** Pose: shoulder yaw (+ left), pitch (+ up), roll; arm offset; wrist twist, bend (+ blade forward), side. */
export const POSE_SIZE = 10;
export const P_YAW = 0;
export const P_PITCH = 1;
export const P_ROLL = 2;
export const P_DX = 3;
export const P_DY = 4;
export const P_DZ = 5;
export const P_TWIST = 6;
export const P_BEND = 7;
export const P_SIDE = 8;
/** 0 = the arm at rest, 1 = raised to the inspect pose (fpArms.ts). */
export const P_HOLD = 9;

type Ease = 'io' | 'out' | 'in' | 'lin';
interface Key {
  t: number;
  p: readonly number[];
  /** How the motion into this key eases. */
  e?: Ease;
}

const REST: readonly number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
const k = (t: number, p: readonly number[], e?: Ease): Key => ({ t, p, e });

/** Seconds; the last key's time is the animation's length (idle loops). */
const ANIMS: Record<KnifeAnim, Key[]> = {
  // Comes up from below with a flick of the wrist, settling with a small bounce.
  draw: [
    k(0, [0.1, -0.5, 0.2, 0.04, -0.3, 0.05, -0.9, 0.4, 0]),
    k(0.3, [0.02, 0.05, 0.03, 0, 0.01, 0, -0.25, -0.1, 0], 'out'),
    k(0.42, [0, -0.02, 0, 0, -0.005, 0, 0.05, 0.03, 0]),
    k(0.55, REST),
  ],
  idle: [k(0, REST), k(1.6, [0.006, 0.012, 0.004, 0, 0.004, 0, 0.03, 0.02, 0]), k(3.2, REST)],
  // Forehand (like CS2's fixed karambit): a little cock to the right, then the arm drives forward
  // and to the left with the blade hooking through, finishing low left.
  slash1: [
    k(0, REST),
    k(0.06, [-0.03, 0.04, 0, 0.03, 0.03, 0.02, -0.1, -0.1, -0.25], 'out'),
    k(0.14, [0.05, 0.05, 0, -0.12, 0.02, -0.15, -0.4, 0.2, 0.4], 'in'),
    k(0.21, [0.08, -0.02, 0, -0.18, -0.02, -0.12, -0.3, 0.2, 0.6], 'out'),
    k(0.4, REST),
  ],
  // Backhand: from the left, the arm sweeps back out to the right with the blade rising.
  slash2: [
    k(0, REST),
    k(0.08, [0.05, 0.04, 0, -0.1, 0.02, -0.08, -0.5, 0.1, 0.5], 'out'),
    k(0.16, [-0.02, 0.03, 0, 0.03, 0.02, -0.12, -0.6, 0.15, -0.2], 'in'),
    k(0.23, [-0.03, 0, 0, 0.05, 0, -0.06, -0.5, 0.1, -0.35], 'out'),
    k(0.4, REST),
  ],
  // Heavy stab: draw the fist back a little with the blade cocked, then thrust it forward into the
  // middle.
  stab: [
    k(0, REST),
    k(0.28, [0, 0.06, 0, 0.02, 0.03, 0.05, -0.2, -0.3, 0.1], 'io'),
    k(0.34, [0, 0.065, 0, 0.02, 0.032, 0.055, -0.22, -0.32, 0.12]),
    k(0.44, [0.06, 0.05, 0, -0.04, 0.03, -0.22, -0.3, 0.5, 0.3], 'in'),
    k(0.56, [0.055, 0.045, 0, -0.04, 0.028, -0.2, -0.3, 0.5, 0.3], 'out'),
    k(0.95, REST),
  ],
  // Inspect, like CS2: the arm comes up to the held pose (palm to the camera, blade hanging down
  // and out to the left; P_HOLD), turns the knife one way then the other, and goes back.
  inspect: [
    k(0, REST),
    k(0.45, [0, 0, 0, 0, 0, 0, 0, 0, 0, 1], 'io'),
    k(1.05, [0, 0, 0, 0, 0.005, 0, 0.12, 0.05, 0, 1]),
    k(1.5, [0, 0, 0, 0, 0.005, 0, -0.45, 0, 0.1, 1], 'io'),
    k(2.05, [0, 0, 0, 0, 0, 0, -0.5, 0, 0.1, 1]),
    k(2.6, REST, 'io'),
  ],
};

export function animLength(a: KnifeAnim): number {
  const keys = ANIMS[a];
  return keys[keys.length - 1].t;
}

const ease = (e: Ease | undefined, x: number) => {
  switch (e) {
    case 'out':
      return 1 - (1 - x) * (1 - x) * (1 - x);
    case 'in':
      return x * x * x;
    case 'lin':
      return x;
    default:
      return x * x * (3 - 2 * x);
  }
};

/** Pose of `a` at `t` seconds into `out` (idle loops; others hold their last key). No allocations. */
export function sampleKnife(a: KnifeAnim, t: number, out: number[]): void {
  const keys = ANIMS[a];
  const len = keys[keys.length - 1].t;
  if (a === 'idle') t = ((t % len) + len) % len;
  if (t <= 0) {
    for (let i = 0; i < POSE_SIZE; i++) out[i] = keys[0].p[i] ?? 0;
    return;
  }
  for (let j = 1; j < keys.length; j++) {
    const b = keys[j];
    if (t > b.t && j < keys.length - 1) continue;
    const a0 = keys[j - 1];
    const x = Math.min(1, Math.max(0, (t - a0.t) / (b.t - a0.t)));
    const f = ease(b.e, x);
    for (let i = 0; i < POSE_SIZE; i++) {
      const from = a0.p[i] ?? 0;
      out[i] = from + ((b.p[i] ?? 0) - from) * f;
    }
    return;
  }
}
